import { useMemo, useState } from "react";

import { BulkExportPayload, SavedBulkExportPayload } from "../../../subscriptions/types";
import { useBulkExportContext } from "../BulkExportProvider";
import { virtualConfiguration } from "../../components/BulkExportStepper";
import { useBulkExportSettingsSave } from "../../shared/hooks/useBulkExportSettingsSave";
import { useDataSourcesQuery, useDimensionssQuery } from "../../api";
import { virtualFrequency } from "../../components/BulkExportStepper/BulkExportStepper.constants";
import { useBulkExportFavouriteQueryParams } from "../../shared/hooks/useBulkExportFavouriteQueryParams";
import { IDimension } from "../../types";

import { validatePayload } from "./SubscriptionProvider.utils";
import {
  ISubscriptionProviderContextValue,
  ISubscriptionProviderContextState,
  ISubscriptionProviderProps,
  ISubscriptionProviderContextActions,
} from "./SubscriptionProvider.types";
import { BulkExportTab } from "../BulkExportProvider/BulkExportProvider.types";

export function useSubscriptionState(props: ISubscriptionProviderProps): ISubscriptionProviderContextValue {
  const { payload } = props;

  const context = useBulkExportContext();

  const { dataSources = [], isDataSourcesLoading } = useDataSourcesQuery();
  const subSourceId = dataSources?.find((ds) => ds.identifier === context?.state?.cubeId)?.id;
  const { dimensions: fetchedDimensions = [], isDimensionsLoading } = useDimensionssQuery(subSourceId);

  const { favoriteBulkId } = useBulkExportFavouriteQueryParams();
  const settings = useBulkExportSettingsSave();

  const stateBulk = context?.state;
  const actionsBulk = context?.actions;
  const { email, cubeId, tab, currentSavedSettings, dataSourceId, currentFavouriteSettings } = stateBulk ?? {};
  const { setDimension } = actionsBulk ?? {};

  const isSaved = tab === BulkExportTab.Saved;
  const currentSourceName = dataSources?.find((ds) => ds.id === dataSourceId)?.name;
  const title = isSaved ? currentSavedSettings?.name! : currentSourceName!;
  const savedSettingId = currentSavedSettings?.id!;

  const [initialPayload] = useState<BulkExportPayload | SavedBulkExportPayload | undefined>(() => payload);

  let dimensions = settings?.dimensions || [];
  let measures = settings?.measures || [];
  if (favoriteBulkId) {
    dimensions = currentFavouriteSettings?.mark?.parameters?.dimensions || [];
    measures = currentFavouriteSettings?.mark?.parameters?.measures || [];
  }

  const currentPayload = useMemo<BulkExportPayload | SavedBulkExportPayload>(() => {
    if (isSaved) {
      return {
        title: title,
        savedSettingId: savedSettingId,
      };
    }
    const allDimensionIds = fetchedDimensions
      .map((dim) => dim.hierarchyIdentifier)
      .filter((dimId) => dimId !== virtualConfiguration.identifier)
      .filter((dimId) => dimId !== virtualFrequency.hierarchyIdentifier)
      .filter((dimId) => dimId !== "value")
      .filter((dimId) => dimId !== "source")
      .filter((dimId) => dimId !== "value_trade");

    const allDimensions: IDimension[] = allDimensionIds.map((dimId): IDimension => {
      const dimData = dimensions.find((dim) => dim.id === dimId);

      if (dimData) {
        return dimData;
      }
      return {
        id: dimId,
        selection: [],
        fieldName: "",
        isSelected: false,
      };
    });
    return {
      title: title,
      dimensions: allDimensions,
      email: email || "",
      cubeId: cubeId || "",
      measures: measures || [],
      name: null,
      subscriptionName: null,
    };
  }, [email, cubeId, JSON.stringify(dimensions), JSON.stringify(measures), title, isSaved, savedSettingId]);

  const applyPayload = useMemo(() => {
    return (payload: BulkExportPayload) => {
      if (!setDimension) {
        return;
      }
      const configuration: string[] = [];
      payload?.dimensions.forEach((dimension) => {
        setDimension(dimension);
        if (dimension.isSelected) {
          configuration.push(`${dimension.id}.name`);
        }
      });
      payload?.measures.forEach((dimension) => {
        configuration.push(`${dimension.id}`);
      });
      const virtual = { fieldName: "", id: virtualConfiguration.identifier, isSelected: true, selection: configuration };
      setDimension(virtual);

      if (
        payload &&
        payload?.dimensions.some((dim) => dim.id === "calendar.default") &&
        payload?.dimensions.some((dim) => dim.id !== virtualFrequency.hierarchyIdentifier)
      ) {
        const calendarFrequency = payload?.dimensions.find((dim) => dim.id === "calendar.default");
        const currentSelection = calendarFrequency && +calendarFrequency?.selection[0]?.toString()?.split("")[0];
        if (typeof currentSelection === "number" && !isNaN(currentSelection)) {
          setDimension({ fieldName: "", id: virtualFrequency.hierarchyIdentifier, isSelected: true, selection: [currentSelection] });
        }
      }
    };
  }, [setDimension, payload]);

  const state = useMemo<ISubscriptionProviderContextState>(
    () => ({
      currentPayload,
      initialPayload,
      isSubscriptionLoading: isDataSourcesLoading || isDimensionsLoading,
      isSubscriptionDisabled: !!validatePayload(currentPayload),
    }),
    [initialPayload, currentPayload, isDataSourcesLoading, isDimensionsLoading]
  );

  const actions = useMemo<ISubscriptionProviderContextActions>(
    () => ({
      applyPayload,
    }),
    [applyPayload]
  );

  const value = useMemo(() => ({ state, actions }), [state, actions]);

  return value;
}
